import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';

import { config } from '@/shared/lib/config';

import type { ClientAPI } from './flow/generated/client-api-types';
import { httpAPI } from './flow/http';

/**
 * RTK Query over flow's copied CQRS client (`./flow`, see `.prettierignore`).
 * Kept apart from `publisherApi`: that one logs the publisher out on a 401,
 * and a flow failure must not.
 */

type Projections = ClientAPI['projection'];
type Commands = ClientAPI['command'];

export type FlowProjectionKey = keyof Projections;
export type FlowCommandKey = keyof Commands;

export type FlowProjectionPayload<K extends FlowProjectionKey> = Parameters<
  Projections[K]
>[0];
export type FlowCommandPayload<K extends FlowCommandKey> = Parameters<
  Commands[K]
>[0];

export type FlowProjectionResult<K extends FlowProjectionKey> = Awaited<
  ReturnType<Projections[K]>
>;
export type FlowCommandResult<K extends FlowCommandKey> = Awaited<
  ReturnType<Commands[K]>
>;

/** flow's `{ success: false, reason }` answers: data, not errors */
export type FlowProjectionRejection<K extends FlowProjectionKey> = Extract<
  FlowProjectionResult<K>,
  { success: false }
>;
export type FlowCommandRejection<K extends FlowCommandKey> = Extract<
  FlowCommandResult<K>,
  { success: false }
>;

/** No answer from flow at all: network, CORS, a non-2xx status, not JSON */
export type FlowError = { message: string };

export function isFlowRejection<R extends { success: boolean }>(
  result: R
): result is Extract<R, { success: false }> {
  return result.success === false;
}

/**
 * Each flow lets in only its own cabinet (its `PUBLISHER_CABINET_ORIGINS`),
 * so a build whose `.env` names none skips flow instead of failing on CORS.
 */
export const isFlowEnabled = Boolean(config.FLOW_URL);

async function toQueryResult<T>(
  request: () => Promise<T>
): Promise<{ data: T } | { error: FlowError }> {
  try {
    return { data: await request() };
  } catch (error) {
    return {
      error: {
        message: error instanceof Error ? error.message : 'Unknown error',
      },
    };
  }
}

export function runProjection<K extends FlowProjectionKey>(
  key: K,
  payload: FlowProjectionPayload<K>
) {
  const projection = httpAPI.projection[key] as (
    payload: FlowProjectionPayload<K>
  ) => Promise<FlowProjectionResult<K>>;

  return toQueryResult(() => projection(payload));
}

export function runCommand<K extends FlowCommandKey>(
  key: K,
  payload: FlowCommandPayload<K>
) {
  const command = httpAPI.command[key] as (
    payload: FlowCommandPayload<K>
  ) => Promise<FlowCommandResult<K>>;

  return toQueryResult(() => command(payload));
}

export const flowApi = createApi({
  reducerPath: 'flow',
  baseQuery: fakeBaseQuery<FlowError>(),
  tagTypes: ['ViadsFlowStatus', 'PublisherSites'],
  endpoints: () => ({}),
});
